/**
 * Network collector (P4).
 *
 * Three independent, best-effort probes assembled under the caller's time
 * budget:
 *   1. Network Information API — effective_type / rtt / downlink / save_data.
 *   2. WebRTC STUN — local candidate IPs + a public-IP hint (srflx candidate).
 *      Reveals VPN/NAT posture the server can cross-check against the HTTP IP.
 *   3. DNS-resolver region — coarse latency timing against a few regional STUN
 *      endpoints; the fastest region hints at the resolver/egress location.
 *
 * Every probe fails open to null/empty — nothing here is allowed to throw or
 * hang the token assembly.
 */

import type { NetworkSignals } from "../types.ts";

/** Public STUN endpoints used for candidate gathering + region timing. */
const STUN_ENDPOINTS: Array<{ region: string; url: string }> = [
  { region: "us", url: "stun:stun.l.google.com:19302" },
  { region: "eu", url: "stun:stun.cloudflare.com:3478" },
  { region: "global", url: "stun:global.stun.twilio.com:3478" },
];

const PRIVATE_IP_RE =
  /^(10\.|127\.|169\.254\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.|::1|fe80:|fc00:|fd[0-9a-f]{2}:)/i;
const IP_RE = /([0-9]{1,3}(?:\.[0-9]{1,3}){3})|([a-f0-9]{1,4}(?::[a-f0-9]{0,4}){2,7})/i;

function emptyNetwork(): NetworkSignals {
  return {
    effective_type: null,
    rtt: null,
    downlink: null,
    save_data: null,
    webrtc_local_ips: [],
    public_ip_hint: null,
    dns_resolver_region: null,
  };
}

/** Synchronous, cheap connection metadata read. */
function readConnection(): Pick<NetworkSignals, "effective_type" | "rtt" | "downlink" | "save_data"> {
  const out = { effective_type: null, rtt: null, downlink: null, save_data: null } as Pick<
    NetworkSignals,
    "effective_type" | "rtt" | "downlink" | "save_data"
  >;
  try {
    const c = (navigator as unknown as {
      connection?: { effectiveType?: string; rtt?: number; downlink?: number; saveData?: boolean };
    }).connection;
    if (c) {
      out.effective_type = c.effectiveType ?? null;
      out.rtt = typeof c.rtt === "number" ? c.rtt : null;
      out.downlink = typeof c.downlink === "number" ? c.downlink : null;
      out.save_data = typeof c.saveData === "boolean" ? c.saveData : null;
    }
  } catch {
    /* unsupported */
  }
  return out;
}

interface RtcResult {
  localIps: string[];
  publicIpHint: string | null;
  region: string | null;
}

/**
 * Gather ICE candidates via a throwaway RTCPeerConnection. Local (host)
 * candidates populate `webrtc_local_ips`; the first server-reflexive (srflx)
 * candidate gives a `public_ip_hint`. We also record which STUN region replied
 * first as a coarse egress hint.
 */
function gatherWebrtc(timeoutMs: number): Promise<RtcResult> {
  return new Promise((resolve) => {
    const result: RtcResult = { localIps: [], publicIpHint: null, region: null };
    const RTC = (window as unknown as { RTCPeerConnection?: typeof RTCPeerConnection })
      .RTCPeerConnection;
    if (!RTC) return resolve(result);

    const seenLocal = new Set<string>();
    let pc: RTCPeerConnection | null = null;
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        pc?.close();
      } catch {
        /* ignore */
      }
      result.localIps = Array.from(seenLocal);
      resolve(result);
    };

    const timer = setTimeout(finish, timeoutMs);

    try {
      pc = new RTC({ iceServers: STUN_ENDPOINTS.map((e) => ({ urls: e.url })) });
      // A data channel is required for candidate gathering to begin.
      pc.createDataChannel("gs");

      pc.onicecandidate = (ev) => {
        if (!ev.candidate) {
          // null candidate = gathering complete
          clearTimeout(timer);
          return finish();
        }
        const cand = ev.candidate.candidate || "";
        const m = cand.match(IP_RE);
        const ip = m ? m[0] : null;
        if (!ip) return;

        if (/typ host/.test(cand)) {
          if (PRIVATE_IP_RE.test(ip) || !ip.includes(".")) {
            // keep private/host candidates (mDNS-obfuscated ones won't match IP_RE)
            seenLocal.add(ip);
          } else {
            seenLocal.add(ip);
          }
        } else if (/typ srflx/.test(cand) || /typ prflx/.test(cand)) {
          if (!PRIVATE_IP_RE.test(ip)) {
            result.publicIpHint = result.publicIpHint ?? ip;
            if (!result.region) {
              const relAddr = cand.match(/raddr ([0-9a-f.:]+)/i);
              void relAddr; // reserved for future correlation
            }
          }
        }
      };

      pc.createOffer()
        .then((offer) => pc!.setLocalDescription(offer))
        .catch(() => {
          clearTimeout(timer);
          finish();
        });
    } catch {
      clearTimeout(timer);
      finish();
    }
  });
}

/**
 * Coarse DNS-resolver / egress region probe: time how fast each regional STUN
 * endpoint produces its first candidate; the fastest region is reported. This
 * is a hint only — the server's IpInfo lookup remains authoritative.
 */
function probeRegion(timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    const RTC = (window as unknown as { RTCPeerConnection?: typeof RTCPeerConnection })
      .RTCPeerConnection;
    if (!RTC) return resolve(null);

    let best: { region: string; ms: number } | null = null;
    let pending = STUN_ENDPOINTS.length;
    const conns: RTCPeerConnection[] = [];
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      for (const c of conns) {
        try {
          c.close();
        } catch {
          /* ignore */
        }
      }
      resolve(best?.region ?? null);
    };

    const timer = setTimeout(finish, timeoutMs);

    for (const ep of STUN_ENDPOINTS) {
      try {
        const pc = new RTC({ iceServers: [{ urls: ep.url }] });
        conns.push(pc);
        const start =
          typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        pc.createDataChannel("rg");
        pc.onicecandidate = (ev) => {
          if (!ev.candidate) return;
          if (!/typ srflx/.test(ev.candidate.candidate || "")) return;
          const ms =
            (typeof performance !== "undefined" && performance.now
              ? performance.now()
              : Date.now()) - start;
          if (!best || ms < best.ms) best = { region: ep.region, ms };
          if (--pending <= 0) {
            clearTimeout(timer);
            finish();
          }
        };
        pc.createOffer()
          .then((o) => pc.setLocalDescription(o))
          .catch(() => {
            if (--pending <= 0) {
              clearTimeout(timer);
              finish();
            }
          });
      } catch {
        if (--pending <= 0) {
          clearTimeout(timer);
          finish();
        }
      }
    }
  });
}

/** Collect all network signals within the given budget. */
export async function collectNetwork(budgetMs = 2500): Promise<NetworkSignals> {
  const out = emptyNetwork();
  Object.assign(out, readConnection());

  const webrtcBudget = Math.min(budgetMs, 2000);
  const regionBudget = Math.min(budgetMs, 1500);

  const [rtc, region] = await Promise.all([
    gatherWebrtc(webrtcBudget).catch(() => ({ localIps: [], publicIpHint: null, region: null })),
    probeRegion(regionBudget).catch(() => null),
  ]);

  out.webrtc_local_ips = rtc.localIps;
  out.public_ip_hint = rtc.publicIpHint;
  out.dns_resolver_region = region;
  return out;
}
